import React, { useRef, useState } from "react";
import { Upload, Loader2, X, Image as ImageIcon } from "lucide-react";
import { api, API } from "../lib/api";
import { toast } from "sonner";

/**
 * ImageUpload — drag & drop OR file picker, uploads to /api/admin/upload,
 * returns the public URL (relative path under /api/files/...) via onChange.
 *
 * Props:
 *  - value: current image URL (relative or absolute)
 *  - onChange: (url|null) => void
 *  - label?: string
 *  - testIdPrefix?: string  (e.g. "pm-image-upload")
 */
export default function ImageUpload({ value, onChange, label, testIdPrefix = "image-upload" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Fichier non valide (image uniquement)");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 8 Mo)");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Image téléversée");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors du téléversement");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) upload(file);
  };

  // Resolve absolute URL for preview
  const previewSrc = value
    ? value.startsWith("http") ? value : `${process.env.REACT_APP_BACKEND_URL}${value}`
    : null;

  return (
    <div data-testid={testIdPrefix}>
      {label && <div className="text-xs text-zinc-400 mb-1.5">{label}</div>}
      {previewSrc ? (
        <div className="relative border border-white/10 bg-[#0a0a0c] overflow-hidden group">
          <img src={previewSrc} alt="" className="w-full h-40 object-cover" />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/60 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              data-testid={`${testIdPrefix}-replace`}
              className="bg-[#d4af37] text-black px-3 py-1.5 text-xs font-semibold"
            >
              Remplacer
            </button>
            <button
              type="button"
              onClick={() => onChange(null)}
              data-testid={`${testIdPrefix}-remove`}
              className="bg-white/10 text-white px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1"
            >
              <X size={12} /> Supprimer
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          data-testid={`${testIdPrefix}-dropzone`}
          className={`w-full border-2 border-dashed transition-all p-6 text-center cursor-pointer ${
            dragOver ? "border-[#d4af37] bg-[#d4af37]/5" : "border-white/10 hover:border-white/20 bg-[#0a0a0c]"
          }`}
        >
          {uploading ? (
            <div className="flex flex-col items-center text-zinc-400 text-sm">
              <Loader2 className="animate-spin mb-2 text-[#d4af37]" size={20} />
              Téléversement…
            </div>
          ) : (
            <div className="flex flex-col items-center text-zinc-500 text-sm">
              <Upload size={20} className="mb-2 text-[#d4af37]" />
              <span className="font-medium text-zinc-300">Cliquez ou glissez une image ici</span>
              <span className="text-xs mt-1">JPG, PNG, WEBP — 8 Mo max</span>
            </div>
          )}
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        data-testid={`${testIdPrefix}-input`}
        onChange={(e) => upload(e.target.files?.[0])}
      />
    </div>
  );
}
